<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="scale-line" :class="{'interpolation': interpolation}">
  <div class="scale-line-ruler">
    <div class="scale-line-track" :style="{width: scaleBarWidth}">
      <span>{{scaleLength}}</span>
    </div>
  </div>

  <div class="scale-line-meta">
    <span v-show="magnification" class="scale-line-meta-item">
      <span class="scale-line-label">{{$t('magnification')}}</span>
      <span class="scale-line-value">{{magnification}}X</span>
    </span>
    <span v-if="mousePosition" class="scale-line-meta-item scale-line-position">
      <span class="scale-line-label">x</span>
      <span class="scale-line-value">{{Math.round(mousePosition[0])}}</span>
      <span class="scale-line-label">y</span>
      <span class="scale-line-value">{{Math.round(mousePosition[1])}}</span>
    </span>
  </div>
</div>
</template>

<script>
import { getMagnificationForZoom } from './magnifications';

export default {
  name: 'scale-line',
  props: {
    image: Object,
    zoom: Number,
    mousePosition: Array
  },
  data() {
    return {
      scaleLineLength: 100
    };
  },
  computed: {
    magnification() {
      let magnification = getMagnificationForZoom(this.image, this.zoom);
      if (!magnification) {
        return null;
      }
      return Math.round(magnification * 100) / 100;
    },
    resolution() {
      let resolution = this.image.physicalSizeX ? this.image.physicalSizeX : 1;
      return Math.pow(2, this.image.zoom - this.zoom) * resolution;
    },
    scaleLength() {
      let length = this.scaleLineLength * this.resolution;
      if (this.image.physicalSizeX) {
        let unit = this.$t('um');
        if (length > 1000) {
          length /= 1000;
          unit = this.$t('mm');
        }
        return `${length.toPrecision(3)} ${unit}`;
      } else {
        return `${Math.round(length * 1000) / 1000} ${this.$t('pixels')}`;
      }
    },
    interpolation() {
      return this.zoom > this.image.zoom;
    },
    scaleBarWidth() {
      return `${this.scaleLineLength}px`;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
.scale-line {
  background: transparent;
  color: $dark-text-primary;
  position: static;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  width: 100%;
  min-width: 0;
  padding: 0.55rem 0.65rem 0.45rem;
  font-size: 0.72rem;
  font-family: "Open Sans", Helvetica, Arial, sans-serif;
  line-height: 1.2;
}

.scale-line-ruler {
  display: flex;
  justify-content: center;
  min-width: 0;
}

.scale-line-track {
  position: relative;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 3.5rem;
  height: 1.2rem;
  border: 1px solid currentColor;
  border-top: none;
  color: $dark-text-primary;
  text-align: center;
}

.scale-line-track::before,
.scale-line-track::after {
  content: '';
  position: absolute;
  top: 0;
  width: 1px;
  height: 0.42rem;
  background: currentColor;
}

.scale-line-track::before {
  left: 0;
}

.scale-line-track::after {
  right: 0;
}

.scale-line-track span {
  position: relative;
  top: -0.2rem;
  display: inline-block;
  max-width: calc(100% - 0.4rem);
  padding: 0 0.25rem;
  background: rgba(16, 25, 39, 0.96);
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scale-line-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  color: $dark-text-secondary;
}

.scale-line-meta-item {
  display: inline-flex;
  align-items: baseline;
  gap: 0.22rem;
  min-width: 0;
  white-space: nowrap;
}

.scale-line-label {
  color: $dark-text-secondary;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.04rem;
}

.scale-line-value {
  color: $dark-text-primary;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
}

.interpolation {
  color: #ff6b6b;
}

.interpolation .scale-line-track,
.interpolation .scale-line-value {
  color: #ff6b6b;
}

.scale-line-position {
  justify-content: flex-end;
  flex-shrink: 1;
  overflow: hidden;
}
</style>
